/**
 * Board data and the drag & drop write path.
 *
 * ORDERING IS SERVER-SIDE. A drop never sends a position; it sends the two
 * neighbours the user saw, and the server computes a LexoRank midpoint between
 * them inside a transaction. Two people dragging at once therefore cannot end
 * up with the same rank, and a stale client cannot overwrite an ordering it
 * never saw.
 *
 * The optimistic update is applied to the *unfiltered* cached board, not to the
 * filtered view on screen, so hidden cards keep their place while a filter is
 * on. Every optimistic write takes a snapshot first and puts it back on failure.
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UUID } from '@/types/api'

import {
  issueKeys,
  patchIssueInCaches,
  v2Api,
  type BoardResponse,
  type Issue,
  type MoveIssueInput,
  type TaskStatus,
} from '@/features/issue'

export interface BoardMove {
  issue: Issue
  toColumnId: UUID
  status?: TaskStatus
  /** The issue the card lands below, and the one it lands above. */
  after: Issue | null
  before: Issue | null
}

/** Apply a move to a cached board so the card is where it was dropped. */
export function applyBoardMove(board: BoardResponse, move: BoardMove): BoardResponse {
  const columns = board.columns.map((column) => ({
    ...column,
    issues: column.issues.filter((entry) => entry.id !== move.issue.id),
  }))

  const targetIndex = columns.findIndex((column) => column.id === move.toColumnId)
  if (targetIndex === -1) return board

  const target = columns[targetIndex]!
  const moved: Issue = {
    ...move.issue,
    column_id: move.toColumnId,
    status: move.status ?? target.legacy_status,
  }

  let position = target.issues.length
  if (move.after) {
    const found = target.issues.findIndex((entry) => entry.id === move.after!.id)
    if (found !== -1) position = found + 1
  } else if (move.before) {
    const found = target.issues.findIndex((entry) => entry.id === move.before!.id)
    position = found === -1 ? 0 : found
  } else {
    position = 0
  }

  const issues = [...target.issues]
  issues.splice(position, 0, moved)
  columns[targetIndex] = {
    ...target,
    issues,
    count: issues.length,
    points: issues.reduce((sum, entry) => sum + (entry.story_points ?? 0), 0),
  }

  for (let index = 0; index < columns.length; index += 1) {
    if (index === targetIndex) continue
    const column = columns[index]!
    if (column.issues.length !== board.columns[index]!.issues.length) {
      columns[index] = {
        ...column,
        count: column.issues.length,
        points: column.issues.reduce((sum, entry) => sum + (entry.story_points ?? 0), 0),
      }
    }
  }

  return { ...board, columns }
}

export function useBoardQuery(slug: string | null, projectKey: string | null, scope: string) {
  return useQuery({
    queryKey: issueKeys.board(slug ?? '', projectKey ?? '', scope),
    queryFn: () => v2Api.board(slug as string, projectKey as string, scope),
    enabled: Boolean(slug && projectKey),
    staleTime: 15_000,
  })
}

/**
 * The drop handler.
 *
 * A WIP-limit refusal and a viewer's 403 both come back as ordinary errors, so
 * the card springs back and the message is shown rather than the board quietly
 * disagreeing with the server.
 */
export function useBoardMove(slug: string | null, projectKey: string | null, scope: string) {
  const client = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const boardKey = issueKeys.board(slug ?? '', projectKey ?? '', scope)

  const mutation = useMutation({
    mutationFn: (move: BoardMove) => {
      const input: MoveIssueInput = { column_id: move.toColumnId }
      if (move.after) input.after_id = move.after.issue_key ?? move.after.id
      if (move.before) input.before_id = move.before.issue_key ?? move.before.id
      return v2Api.moveIssue(slug as string, move.issue.issue_key ?? move.issue.id, input)
    },
    onMutate: async (move) => {
      setError(null)
      await client.cancelQueries({ queryKey: boardKey })
      const snapshot = client.getQueryData<BoardResponse>(boardKey)
      client.setQueryData<BoardResponse>(boardKey, (board) =>
        board ? applyBoardMove(board, move) : board,
      )
      return { snapshot }
    },
    onError: (mutationError, _move, context) => {
      if (context?.snapshot) client.setQueryData(boardKey, context.snapshot)
      setError(mutationError)
    },
    onSuccess: (issue) => {
      if (slug) patchIssueInCaches(client, slug, issue)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: boardKey })
      // A status change is also a backlog change, and any issue list sorted
      // by rank has just gone stale too.
      if (slug) {
        void client.invalidateQueries({ queryKey: issueKeys.backlogs(slug) })
        void client.invalidateQueries({ queryKey: issueKeys.issueLists(slug) })
      }
    },
  })

  return {
    move: mutation.mutate,
    isMoving: mutation.isPending,
    error,
    clearError: useCallback(() => setError(null), []),
  }
}
